import React, { Component, Fragment } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { clone } from 'lodash';

import IconButton from 'material-ui/IconButton';
import FlatButton from 'material-ui/FlatButton';
import DatesIcon from 'material-ui/svg-icons/action/today';
import CloseIcon from 'material-ui/svg-icons/navigation/close';
import moment from 'moment';
import { DateRangePicker } from 'react-dates';
import { incidentOperations } from '../../../redux/incident';
import { configOperations } from '../../../redux/config';

import { fetchIncidents, fetchManyIncidents } from '../../../esri/mapHub';

import DataGrid from '../../common/DataGrid';
import DataGridHeader from '../../common/DataGrid/DataGridHeader';
import ColumnChooser from '../../common/DataGrid/ColumnChooser';
import gridDefinition from '../../../defaults/datagrids/incidents.json';

import FilterContainer from '../../filter/FilterContainer';
import FreeformFilterItem from '../../filter/FreeformFilterItem';
import IncidentSqlFilterDialog from './IncidentSqlFilterDialog';

import DownloadAsCsvButton from '../../common/DownloadAsCsvButton';

import 'react-dates/lib/css/_datepicker.css';
import '../../common/DatePicker/styles.css';

import {
  formatAttribute,
  applyFieldObjectOverrides
} from '../../../utils/helpers';
import { getDomainFilter, getFreeformFilter } from '../../../esri/queryUtils';

import theme from '../../../muiTheme';

class IncidentListContainer extends Component {
  constructor() {
    super();

    // get start date and update grid defs
    const endDate = moment();
    const startDate = moment().startOf('year');
    const endTimeEpoch = moment(endDate, 'x');
    const startTimeEpoch = moment(startDate, 'x');

    gridDefinition.baseQuery = `ModifiedOnDateTime >= ${startTimeEpoch} AND ModifiedOnDateTime <= ${endTimeEpoch}`;

    this.state = {
      loading: null,
      incidents: [],
      isMore: true,
      incidentCount: 0,
      showQuickFilter: false,
      showSqlFilterDialog: false,
      isFilterAppliedToMap: true,
      startDate: moment(startDate),
      endDate: moment(endDate),
      focusedInput: null,
      gridDefinition
    };
  }

  componentDidMount() {
    this.updateGridDefinitionFields();
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      prevProps.fields !== this.props.fields ||
      prevProps.incidentListColumns !== this.props.incidentListColumns ||
      prevProps.preferences.incidentListColumns !==
        this.props.preferences.incidentListColumns
    ) {
      this.updateGridDefinitionFields();
    }
  }

  updateGridDefinitionFields() {
    let incidentListColumns = this.props.incidentListColumns;
    if (
      this.props.preferences &&
      this.props.preferences.incidentListColumns &&
      this.props.preferences.incidentListColumns.length
    ) {
      incidentListColumns = this.props.preferences.incidentListColumns;
    }

    const gridDefinitionFields = this.getGridDefinitionFields(
      incidentListColumns
    );

    this.setState({
      gridDefinition: {
        ...this.state.gridDefinition,
        fields: gridDefinitionFields
      }
    });
  }

  getGridDefinitionFields(incidentListColumns) {
    // loop over incidentListColumns,
    // lookup fields definition object from this.props.fields,
    // and return a new array
    // (intended to update the array used in this.state.gridDefinition.fields)
    if (this.props.fields) {
      return incidentListColumns.map(
        columnName => this.props.fields[columnName]
      );
    }

    return [];
  }

  updateIncidentList = (incidents, incidentCount, isMore) => {
    this.setState({ incidents, incidentCount, isMore });
    this.incidentsHaveLoaded();
  };

  incidentsHaveLoaded = () => {
    this.setState({ loading: false });
  };

  incidentsAreLoading = () => {
    this.setState({ loading: true });
  };

  getBaseQuery(filters, sql) {
    const filterClauses = Object.keys(filters)
      .filter(key => filters[key].values.length)
      .map(key => {
        const filter = filters[key];
        const clauses = filter.fieldNames.map(fieldName => {
          if (filter.type === 'freeform') {
            return getFreeformFilter(fieldName, filter.values);
          }
          return getDomainFilter(fieldName, filter.values);
        });
        return clauses.join(' OR ');
      });

    if (!this.state.gridDefinition.baseQuery && !filterClauses.length) {
      return null;
    }

    const filterGroupClause = filterClauses.length
      ? `(${filterClauses.join(' AND ')})`
      : null;
    const baseClause = this.state.gridDefinition.baseQuery
      ? `(${this.state.gridDefinition.baseQuery})`
      : null;
    const baseQuery = [filterGroupClause, baseClause, sql && sql.filter]
      .filter(q => q)
      .join(' AND ');

    return baseQuery;
  }

  getActions = () => [
    <IconButton
      tooltip="View Incident"
      tooltipPosition="bottom-left"
      key="viewIncidentBtn"
    >
      <i className="grid-action-icon esri-icon-review" />
    </IconButton>
  ];

  toggleQuickFilter = () => {
    this.setState({ showQuickFilter: !this.state.showQuickFilter });
  };

  showSqlFilterDialog = () => {
    this.setState({ showSqlFilterDialog: true });
  };

  updateQuickFilter = filter => {
    this.props.updateIncidentFilterValues(filter);
  };

  handleSqlFilterUpdated = sql => {
    this.props.updateIncidentSqlFilter({ ...sql });
    this.setState({ showSqlFilterDialog: false });
  };

  handleSaveSqlFilter = (name, filter) => {
    this.props.addSqlFilter({ name, filter });
  };

  handleRenameSqlFilter = (existingFilterName, newFilterName) => {
    this.props.renameSqlFilter({ existingFilterName, newFilterName });
  };

  handleDeleteSqlFilter = filterName => {
    this.props.removeSqlFilter(filterName);
  };

  getCsvData = () =>
    new Promise(async (resolve, reject) => {
      try {
        const searchOptions = {
          ...this.props.dataGridFilters.incident,
          ...this.props.appConfig.csvQueryManyOptions,
          page: 0,
          returnGeometry: true
        };

        const incidentFeatures = await fetchManyIncidents(searchOptions);

        const flatIncidents = incidentFeatures.map(featureObj => {
          const formattedAttributes = {};

          for (const key in featureObj.attributes) {
            if (featureObj.attributes.hasOwnProperty(key)) {
              const formattedAttribute = formatAttribute(
                key,
                featureObj.attributes[key],
                'incident',
                this.props.fields,
                true
              );
              const { name, value } = formattedAttribute;
              formattedAttributes[name] = value || '';
            }
          }

          return { ...formattedAttributes };
        });

        resolve(flatIncidents);
      } catch (error) {
        reject(error);
      }
    });

  getSqlClearFilter = () => {
    if (!this.props.sql || !this.props.sql.filter) return null;

    return (
      <FlatButton
        backgroundColor={this.getSqlBackgroundColor()}
        style={styles.sqlClearFilterBtn}
        icon={<CloseIcon style={styles.sqlClearFilterIcon} />}
        onClick={() => this.handleSqlFilterUpdated(null)}
      />
    );
  };

  getSqlFontColor = () => {
    if (!this.props.sql || !this.props.sql.filter) {
      return theme.palette.primary1Color;
    }

    return '#efefef';
  };

  getSqlBackgroundColor = () => {
    if (!this.props.sql || !this.props.sql.filter) {
      return theme.palette.canvasColor;
    }

    return theme.palette.primary1Color;
  };

  getSqlDialog = fields => {
    if (!this.state.showSqlFilterDialog) return null;

    const fieldDefs = clone(fields);

    // Remove "faked" fields as they would result in an invalid query
    delete fieldDefs.POOLatitude;
    delete fieldDefs.POOLongitude;

    return (
      <IncidentSqlFilterDialog
        savedFilters={this.props.preferences.sqlFilters}
        filterState={this.props.sql && this.props.sql.filterState}
        fieldDefinitions={fieldDefs}
        onCancel={() => this.setState({ showSqlFilterDialog: false })}
        onSubmit={this.handleSqlFilterUpdated}
        onSaveFilter={this.handleSaveSqlFilter}
        onRenameFilter={this.handleRenameSqlFilter}
        onDeleteFilter={this.handleDeleteSqlFilter}
      />
    );
  };

  updateDateRange = ({ startDate, endDate }) => {
    // add fix to make sure date picker captures all of the 'end date'
    let fixEndDate;
    let fixStartDate;
    if (endDate !== null) {
      fixEndDate = endDate.endOf('day');
    }
    if (startDate !== null) {
      fixStartDate = startDate.startOf('day');
    }
    const endTimeEpoch = moment(fixEndDate, 'x');
    const startTimeEpoch = moment(fixStartDate, 'x');

    // don't run the update until a valid date is entered
    if (startTimeEpoch.isValid() && endTimeEpoch.isValid) {
      const baseQuery = `ModifiedOnDateTime >= ${startTimeEpoch} AND ModifiedOnDateTime <= ${endTimeEpoch}`;

      const gridDefs = {
        ...this.state.gridDefinition,
        baseQuery
      };

      this.setState({
        startDate,
        endDate,
        gridDefinition: gridDefs
      });
    }
  };

  isDateOutsideRange = date => {
    const endDate = moment();
    if (date > endDate) {
      return true;
    }

    return false;
  };

  render() {
    const {
      loading,
      incidents,
      incidentCount,
      isMore,
      isFilterAppliedToMap,
      gridDefinition,
      startDate,
      endDate,
      focusedInput
    } = this.state;
    const {
      filters,
      sql,
      fields,
      addIncidentListColumn,
      removeIncidentListColumn,
      setInitialIncidentListColumns
    } = this.props;
    const propsStyle = this.props.style;
    const freeformSearchFilter = this.props.filters.freeformSearch;
    const { dataGridOptions } = this.props.appConfig;
    const mapLoaded = this.props.map.loaded;

    if (!mapLoaded) {
      return null;
    }

    if (fields) {
      applyFieldObjectOverrides(fields, 'incident');
    }

    let filterContainer;
    if (this.state.showQuickFilter) {
      filterContainer = <FilterContainer />;
    }

    return (
      <Fragment>
        <div style={styles.headerWrapper}>
          <DataGridHeader
            title="Incidents"
            badgeColor={theme.palette.accent1Color}
            itemCount={incidentCount}
          />

          <div style={styles.headerControlsWrapper}>
            <div style={styles.filterWrapper}>
              {this.getSqlDialog(fields)}
              <FreeformFilterItem
                filterName={freeformSearchFilter.name}
                label={freeformSearchFilter.label}
                fieldNames={freeformSearchFilter.fieldNames}
                type={freeformSearchFilter.type}
                values={freeformSearchFilter.values}
                onChange={this.updateQuickFilter}
              />
              <FlatButton
                style={{ marginLeft: '1rem', color: this.getSqlFontColor() }}
                backgroundColor={this.getSqlBackgroundColor()}
                label="SQL Filter"
                labelStyle={styles.sqlFilterBtn}
                onClick={this.showSqlFilterDialog}
              />
              {this.getSqlClearFilter()}

              <span style={styles.dateRangeWrapper}>
                <IconButton
                  style={{ height: '28px', width: '28px', padding: '0' }}
                  tooltip="Incidents are filtered on 'Modified On Date Time' within the date range."
                >
                  <DatesIcon />
                </IconButton>

                <DateRangePicker
                  small
                  hideKeyboardShortcutsPanel
                  isOutsideRange={date =>
                    this.isDateOutsideRange(date, endDate)
                  }
                  startDate={startDate}
                  startDateId="START_DATE"
                  endDate={endDate}
                  endDateId="END_DATE"
                  onDatesChange={this.updateDateRange}
                  focusedInput={focusedInput}
                  onFocusChange={focusedInput =>
                    this.setState({ focusedInput })
                  }
                  displayFormat="M/D/YYYY"
                  minimumNights={0}
                />
              </span>
            </div>
            <div>
              <DownloadAsCsvButton
                tooltip="Download Incidents as CSV"
                fields={fields}
                exportedFilename="incidents.csv"
                dataPromise={this.getCsvData}
              />
            </div>
          </div>
        </div>
        {filterContainer}

        <DataGrid
          api="incident"
          style={propsStyle}
          pollInterval={dataGridOptions.pollingInterval}
          fieldDefinitions={gridDefinition.fields}
          defaultSort={gridDefinition.defaultSort}
          baseQuery={this.getBaseQuery(filters, sql)}
          fetchItems={fetchIncidents}
          loading={loading}
          items={incidents}
          itemCount={incidentCount}
          isMore={isMore}
          pageSize={dataGridOptions.defaultPageSize}
          attributeFormatter={formatAttribute}
          updateItems={this.updateIncidentList}
          onLoadStart={this.incidentsAreLoading}
          onLoadComplete={this.incidentsHaveLoaded}
          actions={this.getActions()}
          actionsHeader={
            <ColumnChooser
              columns={fields}
              selectedColumns={gridDefinition.fields}
              addColumn={addIncidentListColumn}
              removeColumn={removeIncidentListColumn}
              reorderColumn={addIncidentListColumn}
              resetColumns={setInitialIncidentListColumns}
            />
          }
          isFilterAppliedToMap={isFilterAppliedToMap}
        />
      </Fragment>
    );
  }
}

const styles = {
  headerWrapper: {
    display: 'flex',
    alignItems: 'center',
    flex: '0 0 50px'
  },
  headerControlsWrapper: {
    display: 'flex',
    flex: '1 0 300px',
    marginLeft: '36px'
  },
  filterWrapper: {
    display: 'flex',
    alignItems: 'center',
    flex: '1 0 300px'
  },
  sqlFilterBtn: {
    padding: '8px'
  },
  sqlClearFilterBtn: {
    minWidth: '28px'
  },
  sqlClearFilterIcon: {
    width: '18px',
    height: '18px',
    fill: '#efefef'
  },
  dateRangeWrapper: {
    display: 'flex',
    alignItems: 'center',
    marginLeft: '1rem'
  }
};

const mapStateToProps = state => ({
  map: state.map,
  appConfig: state.config,
  filters: state.incident.filters,
  sql: state.incident.sql,
  dataGridFilters: state.dataGridFilters,
  fields: state.incident.fields,
  incidentListColumns: state.incident.incidentListColumns,
  preferences: state.config.preferences
});

const mapDispatchToProps = function(dispatch) {
  return bindActionCreators(
    {
      ...incidentOperations,
      ...configOperations
    },
    dispatch
  );
};

export default withRouter(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )(IncidentListContainer)
);



// WEBPACK FOOTER //
// ./src/components/incidents/IncidentList/IncidentListContainer.js